import React, { useEffect, useState, useCallback } from 'react';
import { api, FeatureOverride, UserType } from '../api/client';
import PageLoader from '../components/PageLoader';

interface Toggle {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
}

const KNOWN_TOGGLES: { key: string; label: string; description: string }[] = [
  {
    key: 'feature_chat',
    label: 'Чат',
    description: 'Показывать страницу чата в мобильном приложении',
  },
  {
    key: 'feature_payments',
    label: 'Оплаты',
    description: 'Показывать вкладку «Оплаты» в кабинете родителя',
  },
];

const USER_TYPES: UserType[] = ['player', 'coach', 'parent'];

const USER_TYPE_LABELS: Record<UserType, string> = {
  player: 'Игрок',
  coach: 'Тренер',
  parent: 'Родитель',
  admin: 'Администратор',
};

interface UserOption {
  id: string;
  name: string;
}

export default function FeatureTogglesPage() {
  const [toggles, setToggles] = useState<Toggle[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, FeatureOverride[]>>({});
  const [users, setUsers] = useState<Record<UserType, UserOption[]>>({
    player: [],
    coach: [],
    parent: [],
    admin: [],
  });

  const load = useCallback(async () => {
    try {
      const [settings, players, coaches, parents] = await Promise.all([
        api.listSettings(),
        api.listPlayers().catch(() => []),
        api.listCoaches().catch(() => []),
        api.listParents().catch(() => []),
      ]);
      const settingsMap = new Map(settings.map((s) => [s.key, s.value]));

      const merged = KNOWN_TOGGLES.map((t) => ({
        ...t,
        enabled: settingsMap.get(t.key) === 'true',
      }));
      setToggles(merged);

      // Load overrides for each known toggle in parallel.
      const overrideEntries = await Promise.all(
        KNOWN_TOGGLES.map(async (t) => {
          const rows = (await api.listFeatureOverrides(t.key)) ?? [];
          return [t.key, rows] as const;
        }),
      );
      setOverrides(Object.fromEntries(overrideEntries));

      setUsers({
        player: players.map((p) => ({ id: p.id, name: p.name })),
        coach: coaches.map((c) => ({ id: String(c.id), name: c.name })),
        parent: parents.map((p) => ({ id: p.id, name: p.name })),
        admin: [],
      });
    } catch (err) {
      console.error('Failed to load toggles', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (key: string, enabled: boolean) => {
    setSaving(key);
    try {
      await api.upsertSetting(key, enabled ? 'true' : 'false');
      setToggles((prev) =>
        prev.map((t) => (t.key === key ? { ...t, enabled } : t)),
      );
    } catch (err) {
      console.error('Failed to save toggle', err);
    } finally {
      setSaving(null);
    }
  };

  const handleAddOverride = async (
    key: string,
    userType: UserType,
    userID: string,
    enabled: boolean,
  ) => {
    if (!userID) return;
    try {
      await api.upsertFeatureOverride(key, userType, userID, enabled);
      const rows = (await api.listFeatureOverrides(key)) ?? [];
      setOverrides((prev) => ({ ...prev, [key]: rows }));
    } catch (err) {
      console.error('Failed to add override', err);
    }
  };

  const handleRemoveOverride = async (
    key: string,
    userType: UserType,
    userID: string,
  ) => {
    try {
      await api.deleteFeatureOverride(key, userType, userID);
      setOverrides((prev) => ({
        ...prev,
        [key]: (prev[key] ?? []).filter(
          (o) => !(o.user_type === userType && o.user_id === userID),
        ),
      }));
    } catch (err) {
      console.error('Failed to remove override', err);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="page">
      <h2>Функции приложения</h2>
      <p className="page-description">
        Общий переключатель задаёт значение по умолчанию для всех пользователей.
        Персональные исключения ниже включают или выключают функцию для
        конкретных людей поверх общего значения.
      </p>
      <div className="card-list">
        {toggles.map((t) => (
          <div key={t.key} className="toggle-card">
            <div className="toggle-card-header">
              <div className="toggle-info">
                <h3>{t.label}</h3>
                <p>{t.description}</p>
                <code>{t.key}</code>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={t.enabled}
                  disabled={saving === t.key}
                  onChange={(e) => handleToggle(t.key, e.target.checked)}
                />
                <span className="slider" />
              </label>
            </div>
            <OverridesPanel
              featureKey={t.key}
              overrides={overrides[t.key] ?? []}
              users={users}
              onAdd={handleAddOverride}
              onRemove={handleRemoveOverride}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

interface OverridesPanelProps {
  featureKey: string;
  overrides: FeatureOverride[];
  users: Record<UserType, UserOption[]>;
  onAdd: (
    key: string,
    userType: UserType,
    userID: string,
    enabled: boolean,
  ) => Promise<void>;
  onRemove: (key: string, userType: UserType, userID: string) => Promise<void>;
}

function OverridesPanel({
  featureKey,
  overrides,
  users,
  onAdd,
  onRemove,
}: OverridesPanelProps) {
  const [userType, setUserType] = useState<UserType>('player');
  const [userID, setUserID] = useState('');
  const [enabled, setEnabled] = useState(true);

  // Build a quick id → name lookup so removed-user rows still render the id
  // as a fallback name. Indexed by `${user_type}:${user_id}`.
  const nameByKey = new Map<string, string>();
  (Object.keys(users) as UserType[]).forEach((t) => {
    users[t].forEach((u) => {
      nameByKey.set(`${t}:${u.id}`, u.name);
    });
  });

  const options = users[userType] ?? [];
  // Hide users who already have an override on this feature so we don't
  // accidentally stack duplicates — the upsert would handle it, but the
  // admin should use the toggle in the list below instead.
  const takenIDs = new Set(
    overrides.filter((o) => o.user_type === userType).map((o) => o.user_id),
  );
  const availableOptions = options.filter((u) => !takenIDs.has(u.id));

  const handleAdd = async () => {
    if (!userID) return;
    await onAdd(featureKey, userType, userID, enabled);
    setUserID('');
  };

  return (
    <div className="overrides-panel">
      <div className="overrides-header">Персональные исключения</div>
      <div className="overrides-add">
        <select
          value={userType}
          onChange={(e) => {
            setUserType(e.target.value as UserType);
            setUserID('');
          }}
        >
          {USER_TYPES.map((t) => (
            <option key={t} value={t}>
              {USER_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <select value={userID} onChange={(e) => setUserID(e.target.value)}>
          <option value="">Выберите пользователя…</option>
          {availableOptions.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        <label className="overrides-enabled">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span>Включено</span>
        </label>
        <button
          type="button"
          className="overrides-add-btn"
          onClick={handleAdd}
          disabled={!userID}
        >
          Добавить исключение
        </button>
      </div>
      {overrides.length > 0 ? (
        <ul className="overrides-list">
          {overrides.map((o) => {
            const name =
              nameByKey.get(`${o.user_type}:${o.user_id}`) ?? o.user_id;
            return (
              <li
                key={`${o.user_type}:${o.user_id}`}
                className="overrides-row"
              >
                <span className="overrides-user-type">{USER_TYPE_LABELS[o.user_type]}</span>
                <span className="overrides-user-name">{name}</span>
                <span
                  className={`overrides-state ${
                    o.enabled ? 'state-on' : 'state-off'
                  }`}
                >
                  {o.enabled ? 'Включено' : 'Выключено'}
                </span>
                <button
                  type="button"
                  className="overrides-remove-btn"
                  onClick={() =>
                    onRemove(featureKey, o.user_type, o.user_id)
                  }
                >
                  Убрать
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="overrides-empty">Исключений нет — действует общее значение.</p>
      )}
    </div>
  );
}
